import { useEffect, useState } from "react";

import { useGetDataRoomResourceFileForLp } from "@vega-api/generated/coinvest-deal-data-room-files-lp/coinvest-deal-data-room-files-lp";
import type { DataRoomNodeView } from "@vega-api/generated/types/dataRoomNodeView";
import { EmptyCard } from "@vega-core/components/Cards/EmptyCard/EmptyCard";
import { Spinner } from "@vega-core/components/Loaders/LoadingSpinner/Spinner";
import { PDFViewer } from "@vega-core/components/PDFViewer/PDFViewer";
import { m } from "@vega-core/paraglide/messages";
import { trackCoInvestDocumentDownloaded } from "@vega-core/utils/posthog/coInvestAnalytics";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";
import { DataRoomFileTreeCell } from "@vega-modules/scenes/CoInvestments/components/DataRoomFileTree/DataRoomFileTreeCell";
import type { DataRoomFileTreeNode } from "@vega-modules/scenes/CoInvestments/components/DataRoomFileTree/types";
import { useVisibleDataRoomRows } from "@vega-modules/scenes/CoInvestments/components/DataRoomFileTree/useVisibleDataRoomRows";
import { openDataRoomFile } from "@vega-modules/scenes/CoInvestments/utils/openDataRoomFile";

import {
  type DataRoomTreeNode,
  useLpDataRoom,
} from "../../hooks/useLpDataRoom";

type LpDataRoomViewProps = {
  dealOpportunityId: string;
  dataRoomId: string;
};

type LpDataRoomFileTreeNode = DataRoomFileTreeNode & DataRoomNodeView;

function buildLpDataRoomFileTree(rootNodes: DataRoomTreeNode[]) {
  const nodeMap: Record<string, LpDataRoomFileTreeNode | undefined> = {};
  const rootIds: string[] = [];

  function walk(node: DataRoomTreeNode) {
    nodeMap[node.id] = {
      ...node,
      type: node.isFolder ? "folder" : "file",
      children: node.children.map((childNode) => childNode.id),
    };

    node.children.forEach(walk);
  }

  rootNodes.forEach((rootNode) => {
    rootIds.push(rootNode.id);
    walk(rootNode);
  });

  return { nodeMap, rootIds };
}

function getFileExtension(node: DataRoomNodeView): string {
  if (node.file?.fileName) {
    const lastDot = node.file.fileName.lastIndexOf(".");

    if (lastDot !== -1 && lastDot < node.file.fileName.length - 1) {
      return node.file.fileName.slice(lastDot + 1).toUpperCase();
    }
  }

  if (node.file?.contentType) {
    return node.file.contentType;
  }

  return "";
}

export function LpDataRoomView({
  dealOpportunityId,
  dataRoomId,
}: LpDataRoomViewProps) {
  const { rootNodes, isLoading, isError } = useLpDataRoom(
    dealOpportunityId,
    dataRoomId,
  );
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(
    new Set(),
  );
  const showErrorToast = useCoInvestErrorToast();
  const [selectedPdfFile, setSelectedPdfFile] =
    useState<DataRoomNodeView | null>(null);
  const {
    data: pdfFileData,
    isLoading: isPdfUrlLoading,
    isError: isPdfUrlError,
    error: pdfUrlError,
  } = useGetDataRoomResourceFileForLp(
    dealOpportunityId,
    dataRoomId,
    selectedPdfFile?.id ?? "",
    { withDownloadUrl: true },
    { query: { enabled: !!selectedPdfFile?.id } },
  );

  useEffect(() => {
    if (isPdfUrlError) {
      showErrorToast(pdfUrlError as Parameters<typeof showErrorToast>[0]);
    }
  }, [isPdfUrlError, pdfUrlError, showErrorToast]);

  function toggleFolder(id: string) {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }

  function openPdf(node: DataRoomNodeView) {
    setSelectedPdfFile(node);
  }

  function handleOpenFile(node: LpDataRoomFileTreeNode): void {
    if (node.isFolder) return;

    trackCoInvestDocumentDownloaded({
      dealOpportunityId,
      documentId: node.id,
    });

    openDataRoomFile({
      role: "lp",
      dealOpportunityId,
      dataRoomId,
      resourceId: node.id,
      filename: node.name,
    }).catch((error) => {
      showErrorToast(error as Parameters<typeof showErrorToast>[0]);
    });
  }

  const { nodeMap, rootIds } = buildLpDataRoomFileTree(rootNodes);
  const visibleRows = useVisibleDataRoomRows({
    nodeMap,
    rootIds,
    collapsedIds: collapsedFolders,
  });

  if (isLoading) {
    return (
      <div
        className="flex h-48 items-center justify-center"
        data-testid="lp-data-room-loading"
      >
        <Spinner className="w-16 h-16" />
        <span className="sr-only">{m.coInvestments_lpDataRoom_loading()}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="flex h-48 items-center justify-center"
        data-testid="lp-data-room-error"
      >
        <p className="body-base-regular text-text/secondary">
          {m.coInvestments_lpDataRoom_error()}
        </p>
      </div>
    );
  }

  if (rootNodes.length === 0) {
    return (
      <EmptyCard
        description={m.coInvestments_lpDataRoom_empty()}
        title={m.coInvestments_lpDataRoom_emptyTitle()}
      />
    );
  }

  return (
    <>
      <div
        className="w-full overflow-x-auto rounded border border-border/primary"
        data-testid="lp-data-room-view"
      >
        <table
          aria-label={m.coInvestments_lpDataRoom_tableLabel()}
          className="w-full border-collapse"
        >
          <thead>
            <tr className="bg-background/primary">
              <th className="border-b border-border/primary px-2 py-2 text-left body-small-medium text-text/primary">
                {m.coInvestments_lpDataRoom_name()}
              </th>
              <th className="border-b border-border/primary px-2 py-2 text-left body-small-medium text-text/primary w-20">
                {m.coInvestments_lpDataRoom_type()}
              </th>
              <th className="border-b border-border/primary px-2 py-2 text-left body-small-medium text-text/primary w-28">
                {m.coInvestments_lpDataRoom_date()}
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((node) => {
              const isFolder = node.type === "folder";
              const dateText = node.createdAt
                ? new Date(node.createdAt).toLocaleDateString()
                : "";

              return (
                <tr
                  className={
                    isFolder
                      ? "bg-background/secondary"
                      : "bg-background/primary"
                  }
                  data-testid={
                    isFolder ? `folder-row-${node.id}` : `file-row-${node.id}`
                  }
                  key={node.id}
                >
                  <DataRoomFileTreeCell
                    cellTestId={`lp-data-room-tree-row-${node.id}`}
                    collapseToggleTestId={`folder-toggle-${node.id}`}
                    depth={node.depth}
                    folderToggleAriaLabel={node.name}
                    indentPx={16}
                    isCollapsed={collapsedFolders.has(node.id)}
                    node={node}
                    onOpenFile={handleOpenFile}
                    onOpenPdf={openPdf}
                    onToggleCollapse={() => toggleFolder(node.id)}
                  />
                  <td className="h-9 border-b border-border/primary px-2 body-base-regular text-text/secondary">
                    {isFolder ? "" : getFileExtension(node)}
                  </td>
                  <td className="h-9 border-b border-border/primary px-2 body-base-regular text-text/secondary">
                    {isFolder ? "" : dateText}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {selectedPdfFile !== null ? (
        <PDFViewer
          blobOrString={pdfFileData?.data.file.url}
          downloadFileName={selectedPdfFile.name}
          isDownloadEnabled={selectedPdfFile.isDownloadable ?? false}
          isLoading={isPdfUrlLoading}
          isOpen
          setIsOpen={(open) => {
            if (!open) {
              setSelectedPdfFile(null);
            }
          }}
        />
      ) : null}
    </>
  );
}
